  <template>
  <div class="tables-page-container">
    <!-- Main Content Area with Left (Stats, Filter, Table List) and Right (Full Height) Panels -->
    <div class="main-content-area">
      <v-container fluid class="content-container pa-0">
        <v-row class="content-row" no-gutters>
          <!-- Left Panel - Stats, Filter, and Scrollable Table List -->
          <v-col cols="8" class="left-panel">
            <!-- Fixed Statistics Section -->
            <div class="fixed-stats-section pr-2">
              <KpiCards
                :items="tableKpiItems"
                clickable
                :active-key="activeStatFilter || 'total'"
                @select="
                  (stat) => filterByStatus(stat.key === 'total' ? '' : stat.key)
                "
              />
            </div>

            <!-- Fixed Filter Section -->
            <div class="fixed-filter-section pr-2 pt-2">
              <v-card class="rounded-lg" outlined flat :elevation="0">
                <v-card-title>
                  <v-row justify="space-between" align="center">
                    <v-col cols="12" md="3">
                      <TextSelect
                        v-model="zone_filter"
                        :items="translatedZoneList"
                        :dense="true"
                        :placeholder="false"
                        text="name"
                        itemValue="id"
                        :returnObject="false"
                        label="'filter_by_zone"
                      />
                    </v-col>

                    <v-col cols="12" md="5">
                      <TextSearch v-model="search" @search="fetchItem()" />
                    </v-col>

                    <v-col
                      v-if="canRepositionTables"
                      cols="12"
                      md="1"
                      class="d-flex align-center justify-end"
                    >
                      <v-switch
                        class="pb-3"
                        v-model="dragEnabled"
                        color="primary"
                        hide-details
                        dense
                        inset
                      />
                    </v-col>
                    <v-col cols="12" md="3" class="text-right">
                      <v-btn
                        depressed
                        color="primary"
                        class="rounded-lg ps-btn-primary"
                        @click="newItem"
                      >
                        <v-icon left>fal fa-plus-circle</v-icon>
                        {{ $t("new_data") }}
                      </v-btn>
                    </v-col>
                  </v-row>
                </v-card-title>
              </v-card>
            </div>

            <!-- Scrollable Table Cards List -->
            <div class="table-cards-container">
              <!-- Skeleton (first load / refetch) -->
              <div
                v-if="loading && !(items.data && items.data.length)"
                class="d-flex flex-wrap"
                aria-hidden="true"
              >
                <v-col
                  v-for="n in 10"
                  :key="`table-skeleton-${n}`"
                  class="cols-5 pl-0 pr-2 pb-0"
                >
                  <div class="ps-table-card ps-table-card--skeleton">
                    <div class="d-flex align-start justify-space-between">
                      <div class="flex-grow-1">
                        <div
                          class="ps-skeleton-line ps-skeleton-line--w60"
                        ></div>
                        <div
                          class="ps-skeleton-line ps-skeleton-line--w40 mt-2"
                        ></div>
                      </div>
                      <div class="ps-skeleton-pill"></div>
                    </div>
                    <div
                      class="ps-skeleton-line ps-skeleton-line--w50 mt-4"
                    ></div>
                  </div>
                </v-col>
              </div>

              <!-- Table Cards List -->
              <v-row v-else-if="items.data && items.data.length > 0">
                <draggable
                  v-model="draggableTables"
                  :disabled="!canRepositionTables || !dragEnabled"
                  group="tables"
                  ghost-class="table-ghost"
                  chosen-class="table-chosen"
                  drag-class="table-drag"
                  @start="onDragStart"
                  @end="onDragEnd"
                  @change="handleTableChange"
                  handle=".drag-handle"
                  class="d-flex flex-wrap"
                >
                  <v-col
                    v-for="(item, index) in draggableTables"
                    :key="index"
                    class="cols-5 pl-0 pr-2 pb-0"
                  >
                    <v-card
                      :class="[
                        'table-card ps-table-card',
                        `table-card-${item.status}`,
                        {
                          'table-selected':
                            selectedTable && selectedTable.id === item.id,
                          'dragging-active':
                            isDragging && draggedTable === item.id,
                        },
                      ]"
                      outlined
                      flat
                      :elevation="0"
                      @click="selectTable(item)"
                    >
                      <v-card-text class="pa-0">
                        <!-- New Order Indicator Dot -->
                        <div
                          v-if="
                            item.active_ticket &&
                            item.active_ticket.is_new_order
                          "
                          class="new-order-indicator"
                        ></div>

                        <!-- Drag Handle (only shown when drag mode is enabled and user has permission) -->
                        <div
                          v-if="canRepositionTables && dragEnabled"
                          class="drag-handle ps-table-card__drag"
                        >
                          <v-icon color="grey" size="18" class="drag-icon">
                            fal fa-layer-group
                          </v-icon>
                        </div>

                        <!-- Top-down "real table": chairs around a tabletop -->
                        <div
                          class="ps-table-visual"
                          :class="`ps-table-visual--${item.status}`"
                        >
                          <div class="ps-table-visual__chairs">
                            <span
                              v-for="n in seatLayout(item).top"
                              :key="`t${n}`"
                            ></span>
                          </div>
                          <div class="ps-table-visual__top">
                            <span
                              v-if="seatLayout(item).left"
                              class="ps-table-visual__side ps-table-visual__side--l"
                            ></span>
                            <span
                              v-if="seatLayout(item).right"
                              class="ps-table-visual__side ps-table-visual__side--r"
                            ></span>
                            <div class="ps-table-card__name">
                              {{ item.name }}
                            </div>
                            <div
                              v-if="item.zone"
                              class="ps-table-card__zone"
                            >
                              {{ item.zone.name }}
                            </div>
                            <div
                              v-if="
                                (item.status === 'reserved' && item.note) ||
                                (item.status !== 'reserved' &&
                                  item.active_ticket &&
                                  item.active_ticket.total_price)
                              "
                              class="ps-table-card__price"
                            >
                              <span
                                v-if="item.status === 'reserved'"
                                class="ps-table-card__note"
                                >{{ item.note }}</span
                              >
                              <template v-else>
                                {{
                                  item.active_ticket.total_price | NumberFormat
                                }}
                              </template>
                            </div>
                          </div>
                          <div class="ps-table-visual__chairs">
                            <span
                              v-for="n in seatLayout(item).bottom"
                              :key="`b${n}`"
                            ></span>
                          </div>
                        </div>
                      </v-card-text>

                      <v-card-actions>
                        <v-spacer></v-spacer>
                        <v-btn
                          v-if="item.status === 'available'"
                          icon
                          small
                          @click.stop="bookTableFromCard(item)"
                        >
                          <v-icon small>fas fa-calendar-alt</v-icon>
                        </v-btn>
                        <v-btn icon small @click.stop="generateQR(item)">
                          <v-icon small>fas fa-qrcode</v-icon>
                        </v-btn>
                        <v-btn
                          v-if="isBuffet && item.active_ticket"
                          icon
                          small
                          @click.stop="openBuffetEditForTableCard(item)"
                        >
                          <v-icon small>fas fa-edit</v-icon>
                        </v-btn>
                        <v-menu offset-y>
                          <template v-slot:activator="{ on, attrs }">
                            <v-btn
                              icon
                              small
                              v-bind="attrs"
                              v-on="on"
                              @click.stop
                            >
                              <v-icon small>fas fa-ellipsis-v</v-icon>
                            </v-btn>
                          </template>
                          <v-list dense>
                            <v-list-item @click="viewItem(item)">
                              <v-list-item-icon
                                ><v-icon small
                                  >fas fa-eye</v-icon
                                ></v-list-item-icon
                              >
                              <v-list-item-title>{{
                                $t("description")
                              }}</v-list-item-title>
                            </v-list-item>
                            <v-list-item @click="editItem(item)">
                              <v-list-item-icon
                                ><v-icon small
                                  >fas fa-edit</v-icon
                                ></v-list-item-icon
                              >
                              <v-list-item-title>{{
                                $t("edit")
                              }}</v-list-item-title>
                            </v-list-item>
                            <v-list-item @click="deleteItem(item)">
                              <v-list-item-icon
                                ><v-icon small
                                  >fas fa-trash-alt</v-icon
                                ></v-list-item-icon
                              >
                              <v-list-item-title>{{
                                $t("delete")
                              }}</v-list-item-title>
                            </v-list-item>
                          </v-list>
                        </v-menu>
                      </v-card-actions>
                    </v-card>
                  </v-col>
                </draggable>
              </v-row>

              <!-- Empty State -->
              <v-card
                v-else-if="!loading"
                class="pa-8 text-center"
                rounded="lg"
                flat
                outlined
                :elevation="0"
              >
                <v-icon size="64" color="grey lighten-1"
                  >fal fa-layer-group</v-icon
                >
                <div class="text-h6 mt-3">{{ $t("no_data") }}</div>
              </v-card>

              <!-- Infinity Scroll Loading Indicator -->
              <v-row
                v-if="loading && items.data && items.data.length > 0"
                class="mt-2 mb-4"
              >
                <v-col cols="12" class="text-center">
                  <v-progress-circular
                    indeterminate
                    color="primary"
                    size="24"
                  ></v-progress-circular>
                  <div class="text-caption mt-1">Loading more tables...</div>
                </v-col>
              </v-row>
            </div>
          </v-col>

          <!-- Right Panel - Full Height Table Details -->
          <v-col cols="4" class="right-panel">
            <v-card
              rounded="lg"
              flat
              outlined
              :elevation="0"
              class="right-panel-card"
            >
              <!-- Secondary actions: utility buttons (Kitchen, Receipt, Discount, Open Table) -->
              <div class="action-bar-secondary px-2 pt-2 pb-1">
                <v-row no-gutters>
                  <v-col
                    v-for="button in secondaryButtons"
                    :key="button.key"
                    class="pa-1"
                  >
                    <v-btn
                      block
                      outlined
                      depressed
                      class="secondary-action-btn"
                      :aria-label="$t(button.label)"
                      :disabled="buttonDisabledStates[button.key]"
                      @click="executeAction(button.method)"
                    >
                      <div class="d-flex flex-column align-center">
                        <span class="secondary-action-icon">
                          <v-icon size="15">{{ button.icon }}</v-icon>
                        </span>
                        <span class="secondary-btn-label mt-1">{{
                          $t(button.label)
                        }}</span>
                      </div>
                    </v-btn>
                  </v-col>
                </v-row>
              </div>

              <!-- Primary actions: main workflow buttons (Checkout, Add Order, Move Order) -->
              <div class="action-bar-primary px-2 pb-2">
                <v-row no-gutters>
                  <v-col
                    v-for="button in primaryButtons"
                    :key="button.key"
                    class="pa-1"
                  >
                    <v-btn
                      block
                      depressed
                      :color="button.color === 'primary' ? 'primary' : undefined"
                      :outlined="button.color !== 'primary'"
                      class="primary-action-btn"
                      :class="{
                        'primary-action-btn--solid': button.color === 'primary',
                      }"
                      :aria-label="$t(button.label)"
                      :disabled="buttonDisabledStates[button.key]"
                      @click="executeAction(button.method)"
                    >
                      <v-icon left size="15">{{ button.icon }}</v-icon>
                      {{ $t(button.label) }}
                    </v-btn>
                  </v-col>
                </v-row>
              </div>

              <!-- Empty State -->
              <div v-if="!selectedTable" class="empty-state-centered">
                <div class="empty-state-icon-wrap mb-4">
                  <v-icon size="36" color="primary lighten-2">fal fa-hand-pointer</v-icon>
                </div>
                <div class="text-subtitle-1 font-weight-semibold mb-1 grey--text text--darken-1">
                  {{ $t("select_table_to_view") }}
                </div>
                <div class="text-caption grey--text text-center px-4">
                  {{ $t("click_table_for_details") }}
                </div>
              </div>

              <!-- Enhanced Selected Table Content -->
              <div v-else class="enhanced-sidebar">
                <!-- Loading indicator for table details -->
                <div v-if="loadingTableDetails" class="pa-4 text-center">
                  <v-progress-circular
                    indeterminate
                    color="primary"
                    size="24"
                  ></v-progress-circular>
                  <div class="text-caption mt-2">Loading table details...</div>
                </div>

                <!-- 1. ORDER DETAIL (Fixed Position) -->
                <div
                  class="order-detail-section"
                  v-if="order_table && order_table.code"
                >
                  <div class="order-info-card mx-3 my-2">
                    <!-- Header: badge + code + time in one compact row -->
                    <div class="order-info-header px-3 pt-2 pb-2">
                      <div class="d-flex align-center justify-space-between">
                        <div class="d-flex align-center" style="min-width:0;gap:8px">
                          <div class="order-name-badge">
                            <v-icon color="white" size="12">fal fa-utensils</v-icon>
                            <span class="ml-1">{{ selectedTable.name }}</span>
                          </div>
                          <span class="order-txn-code text-truncate">{{ order_table.code }}</span>
                        </div>
                        <span class="order-meta-time flex-shrink-0 ml-2">
                          {{ order_table.created_at | moment }}
                        </span>
                      </div>
                    </div>

                
                    <!-- Totals block -->
                    <div class="order-totals-block px-3 py-2">
                      <!-- Sub-total rows — only shown when there are modifiers -->
                      <template v-if="discountAmount > 0 || vatAmount > 0">
                        <div class="totals-sub-row">
                          <span class="totals-sub-label">{{ $t("total_price") }}</span>
                          <span class="totals-sub-value">
                            {{ totalPriceBeforeDiscount | NumberFormat }}
                            {{ currencySymbol?.symbol ?? "₭" }}
                          </span>
                        </div>
                        <div v-if="discountAmount > 0" class="totals-sub-row">
                          <span class="totals-sub-label red--text">
                            {{
                              appliedDiscount && appliedDiscount.type === "percentage"
                                ? `${$t("discount")} (${appliedDiscount.value}%)`
                                : $t("discount")
                            }}
                          </span>
                          <span class="totals-sub-value red--text">
                            -{{ discountAmount | NumberFormat }}
                            {{ currencySymbol?.symbol ?? "₭" }}
                          </span>
                        </div>
                        <div v-if="vatAmount > 0" class="totals-sub-row">
                          <span class="totals-sub-label">{{ $t("tax") }} ({{ vatRate }}%)</span>
                          <span class="totals-sub-value green--text">
                            +{{ vatAmount | NumberFormat }}
                            {{ currencySymbol?.symbol ?? "₭" }}
                          </span>
                        </div>
                      </template>

                      <!-- Grand total — always shown -->
                      <div class="totals-grand-row" :class="{ 'has-divider': discountAmount > 0 || vatAmount > 0 }">
                        <span class="totals-grand-label">{{ $t("grand_total") }}</span>
                        <span class="totals-grand-value primary--text">
                          {{ grandTotalWithVat | NumberFormat }}
                          {{ currencySymbol?.symbol ?? "₭" }}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>

               
                <!-- 3. ORDER ITEMS LIST (Scrollable Data Table) -->
                <div class="order-items-section">
                  <v-card-text class="pa-0 order-items-card-text">
                    <div
                      class="d-flex justify-space-between align-center my-2 px-3"
                    >
                      <div class="d-flex align-center justify-space-between">
                        <div class="text-subtitle-2">
                          {{ $t("order_items") }}
                        </div>
                        <v-chip
                          v-if="order_table && totalSidebarLineCount > 0"
                          x-small
                          color="primary"
                          class="ml-2 items-count-chip"
                        >
                          {{ totalSidebarLineCount }}
                        </v-chip>
                      </div>
                      <div class="d-flex align-center gap-2">
                        <v-btn
                          v-if="hasSelectedItems"
                          small
                          :disabled="allItemsCanceled"
                          outlined
                          color="error"
                          @click="cancelSelectedItems"
                          class="mr-2"
                        >
                          <v-icon left small>fas fa-times</v-icon>
                          {{ $t("cancel") }}
                        </v-btn>
                        <v-btn
                          v-if="hasSelectedItems"
                          small
                          :disabled="allItemsDelivered"
                          outlined
                          color="success"
                          @click="serveSelectedItems"
                          class="mr-2"
                        >
                          <v-icon left small>fas fa-check</v-icon>
                          {{ $t("served") }}
                        </v-btn>
                        <v-btn
                          v-if="hasSelectedItems"
                          small
                          outlined
                          color="primary"
                          @click="printSelectedItems"
                        >
                          <v-icon left small>fal fa-print</v-icon>
                          {{ $t("print_bill") }}
                        </v-btn>
                      </div>
                    </div>
                    <div class="order-items-table pa-3">
                      <div
                        v-if="
                          isBuffet &&
                          order_table &&
                          order_table.buffetPlans &&
                          order_table.buffetPlans.length
                        "
                        class="buffet-plans-block buffet-plans-block--card pa-3 mb-3 rounded-lg"
                      >
                        <div class="d-flex align-center justify-space-between mb-2">
                          <div class="d-flex align-center">
                            <v-icon small color="primary" class="mr-2">
                              fal fa-utensils
                            </v-icon>
                            <span class="text-subtitle-2">{{ $t("buffet_plans") }}</span>
                          </div>
                          <v-chip small label outlined color="primary">
                            <v-icon left small>fal fa-user-friends</v-icon>
                            {{ totalBuffetPersons }} {{ $t("buffet_persons") }}
                          </v-chip>
                        </div>
                        <div
                          v-for="(p, i) in order_table.buffetPlans"
                          :key="p.id || i"
                          class="buffet-plan-row d-flex align-center justify-space-between"
                        >
                          <div
                            class="buffet-plan-row__left d-flex align-center"
                            style="min-width: 0"
                          >
                            <v-chip
                              small
                              label
                              outlined
                              color="primary"
                              class="mr-2 flex-shrink-0"
                            >
                              {{ p.plan_name }}
                            </v-chip>
                            <span
                              class="text-caption grey--text text--darken-1 text-truncate"
                            >
                              {{ p.unit_price | NumberFormat }}
                              {{ currencySymbol?.symbol ?? "₭" }}
                              <v-icon small class="mx-1">fal fa-user-friends</v-icon>
                              × {{ p.persons }}
                            </span>
                          </div>
                          <div
                            class="text-body-2 font-weight-bold primary--text ml-2 flex-shrink-0"
                          >
                            {{ p.subtotal | NumberFormat }}
                            {{ currencySymbol?.symbol ?? "₭" }}
                          </div>
                        </div>
                      </div>
                      <v-simple-table
                        v-if="
                          order_table &&
                          order_table.history &&
                          order_table.history.length > 0
                        "
                        dense
                        class="scrollable-table"
                      >
                        <template v-slot:default>
                          <thead>
                            <tr>
                              <th class="text-center px-0">
                                <v-checkbox
                                  v-model="allSelectedState"
                                  dense
                                  width="20"
                                  height="20"
                                  hide-details
                                ></v-checkbox>
                              </th>
                              <th class="text-left px-0">{{ $t("no") }}</th>
                              <th class="text-left px-0">
                                {{ $t("menu_name") }}
                              </th>
                              <th class="text-center px-0">{{ $t("qty") }}</th>
                              <th class="text-center px-0">
                                {{ $t("status") }}
                              </th>
                              <th class="text-center">{{ $t("price") }}</th>
                              <th class="text-right px-0">{{ $t("time") }}</th>
                            </tr>
                          </thead>
                          <tbody>
                            <tr
                              v-for="(item, index) in order_table.history"
                              :key="item.id"
                              class="pa-3"
                            >
                              <td class="px-0">
                                <v-checkbox
                                  v-model="item.selected"
                                  width="20"
                                  height="20"
                                  dense
                                  hide-details
                                ></v-checkbox>
                              </td>
                              <td class="px-0 text-left">
                                {{ index + 1 }}
                              </td>
                              <td class="px-0">
                                <div
                                  class="text-body-3 text-capitalize line-clamp-1"
                                >
                                  {{
                                    item.product_id
                                      ? getProductDisplayName(item.product_id)
                                      : $t("unknown_product")
                                  }}
                                </div>
                                <div
                                  v-if="
                                    item.product_variant &&
                                    item.product_variant.sku
                                  "
                                  class="text-caption text--secondary mt-1"
                                >
                                  SKU: {{ item.product_variant.sku }}
                                </div>
                              </td>
                              <td class="px-0 text-center">
                                {{ item.qty }}
                              </td>
                              <td class="px-0 text-center">
                                <div
                                  :class="[
                                    `${
                                      getStatusColorByType(item.status, 'item')
                                        .color
                                    }--text`,
                                  ]"
                                  class="text-caption pa-2"
                                >
                                  {{ $t(item.status) }}
                                </div>
                              </td>
                              <td class="px-0 text-center">
                                <v-chip
                                  v-if="isBuffetIncludedLine(item)"
                                  x-small
                                  label
                                  outlined
                                  color="primary"
                                  class="rounded-lg"
                                >
                                  {{ $t("free") }}
                                </v-chip>
                                <span v-else class="text-caption">
                                  {{
                                    (item.price_sale * item.qty) | NumberFormat
                                  }}
                                </span>
                              </td>
                              <td class="text-right px-0">
                                <span class="text-caption">
                                  {{ item.created_at | momentTime }}
                                </span>
                              </td>
                            </tr>
                          </tbody>
                        </template>
                      </v-simple-table>

                      <div
                        v-else-if="showDesktopNoProductLinesEmpty"
                        class="pa-4 text-center grey--text"
                      >
                        {{ $t("no_orders") }}
                      </div>
                    </div>
                  </v-card-text>
                </div>
              </div>
            </v-card>
          </v-col>
        </v-row>
      </v-container>
    </div>

    <!-- Dialog Components -->
    <app-custom-dialog
      v-model="dialog"
      :footer="false"
      title="description"
      @submit="submitForm"
    >
      <div slot="activator" class="px-6 py-3">
        <v-row>
          <v-col cols="4">{{ $t("table_id") }}</v-col>
          <v-col cols="8">{{ form.id }}</v-col>
          <v-col cols="4">{{ $t("table_name") }}</v-col>
          <v-col cols="8">{{ form.name }}</v-col>
          <v-col cols="4">{{ $t("status") }}</v-col>
          <v-col cols="8">
            <v-chip
              label
              :color="tableStatusColor(form.status)"
              small
              class="white--text"
            >
              {{ $t(form.status) }}
            </v-chip>
          </v-col>
          <v-col cols="4">{{ $t("description") }}</v-col>
          <v-col cols="8">{{ form.note ?? "..." }}</v-col>
          <v-col cols="4"> {{ $t("item") }} </v-col>
          <v-col cols="8">
            <v-chip
              v-if="form.active_ticket"
              color="primary"
              text-color="white"
              class="ma-1"
              small
            >
              {{ form.active_ticket.txn }}
            </v-chip>
          </v-col>
          <v-col cols="4">{{ $t("date") }}</v-col>
          <v-col cols="8">{{ form.created_at | moment }}</v-col>
        </v-row>
      </div>
    </app-custom-dialog>

    <app-custom-dialog
      v-model="dialogAdd"
      :footer="true"
      :title="titleStatus"
      @submit="submitForm"
    >
      <div slot="activator">
        <TTextField
          label="table"
          :errorMessages="tableNameError"
          v-model="form.name"
          @input="$v.form && $v.form.name && $v.form.name.$touch()"
          @blur="$v.form && $v.form.name && $v.form.name.$touch()"
        />
        <TTextField
          class="mt-3"
          label="seats"
          type="number"
          min="1"
          max="20"
          v-model.number="form.seats"
        />
        <TextSelect
          class="mt-3"
          label="table_zone"
          v-model="form.zone_id"
          :items="zone_list"
          text="name"
          itemValue="id"
          :returnObject="false"
        />
      </div>
    </app-custom-dialog>

    <app-custom-dialog
      v-model="reservedModal"
      :footer="true"
      :title="statusTitle"
      @submit="status === 'cancel' ? makePayment() : reserved()"
    >
      <div slot="activator">
        <v-row>
          <v-col cols="12">
            <v-textarea
              v-model="note"
              :label="$t('booking_note')"
              outlined
              color="primary"
              rows="3"
              hide-details="auto"
            ></v-textarea>
          </v-col>
        </v-row>
      </div>
    </app-custom-dialog>

    <app-custom-dialog
      v-model="paymentModal"
      :footer="false"
      :title="actionHandler"
      :width="actionHandler === 'customer' ? '650px' : '500px'"
    >
      <div slot="activator">
        <!-- back fab icon button -->
        <v-btn
          icon
          @click="actionHandler = 'payment'"
          v-if="actionHandler !== 'payment'"
        >
          <v-icon>fal fa-arrow-left</v-icon>
        </v-btn>
        <!-- payment -->
        <template v-if="actionHandler === 'payment'">
          <UnifiedPaymentPad
            :total-amount="amountDue"
            :store-type="store_type"
            :active-currencies="activeCurrencies"
            :applied-discount="appliedDiscount"
            @submit="handlePaymentSubmit"
            @paymentAmountUpdated="handlePaymentAmountUpdated"
            @manualPaymentSuccess="handleManualPaymentSuccess"
            @currency-change="onCurrencyChange"
            @customer="openCreateCustomerDialog"
            @openNoted="openNoted"
            @exchangeRate="openExchangeRate"
            @switchToCash="handleSwitchToCash"
            ref="paymentPad"
          />
        </template>
        <!-- customer -->
        <template v-if="actionHandler === 'customer'">
          <AppCustomer @selectCustomer="actionHandler = 'payment'" />
        </template>
        <!-- note -->
        <template v-if="actionHandler === 'note'">
          <app-text-area v-model="note" placeholder="noted" />
        </template>
        <!-- exchange rate -->
        <template v-if="actionHandler === 'exchangeRate'">
          <NumericExchangeRate
            :totalPrice="amountDue"
            :currencies="activeCurrencies"
            :productCurrency="actualProductCurrency"
            :debug="true"
            @submit="submitExchangeRate"
            @currency-change="onCurrencyChange"
          />
        </template>
      </div>
    </app-custom-dialog>

    <app-custom-dialog
      v-model="dialogMoveMerge"
      :title="tableAction"
      :is_translate="false"
      width="1000px"
      @submit="moveMergeTable()"
    >
      <div slot="activator">
        <app-choose-table
          :tables="tables"
          :table_id="table_id"
          :order_id="order_id"
          @setTable="setTable"
          @mergeTable="mergeTable"
        />
      </div>
    </app-custom-dialog>

    <app-custom-dialog
      v-model="show_qr"
      :footer="false"
      title="table"
      width="450px"
    >
      <div slot="activator">
        <div class="qr-dialog-content">
          <!-- Table Name -->
          <div class="text-h5 font-weight-bold mb-4 primary--text text-center">
            {{ form.name || $t("table") }}
          </div>

          <!-- QR Code -->
          <div class="qr-code-wrapper mb-4">
            <div class="qr-code-container">
              <v-img
                :src="qr"
                width="200"
                height="200"
                class="mx-auto qr-code-image"
              />
            </div>
          </div>

          <!-- Copy Button -->
          <div class="mb-4">
            <v-btn
              block
              outlined
              color="primary"
              @click="copyQR(qr)"
              class="text-capitalize"
            >
              <v-icon left small> fal fa-copy </v-icon>
              {{ $t("copy") }}
            </v-btn>
          </div>

          <!-- Switch - Inset Style -->
          <div class="mb-4">
            <v-switch
              v-model="form.allow_qr_order"
              :label="$t('allow_qr_order')"
              color="primary"
              hide-details
              inset
              @change="toggleQrOrder"
            ></v-switch>
          </div>

          <!-- Print QR Button -->
          <div>
            <v-btn
              block
              color="primary"
              @click="printQR"
              class="text-capitalize"
            >
              <v-icon left small> fal fa-print </v-icon>
              {{ $t("print_qr") }}
            </v-btn>
          </div>
        </div>
      </div>
    </app-custom-dialog>

    <app-custom-dialog
      width="750px"
      v-model="billDetail"
      :footer="false"
      :is_translate="false"
      :title="bill_item"
    >
      <div slot="activator">
        <v-row>
          <v-col cols="12">
            <v-card rounded="lg">
              <v-card-text class="py-0">
                <v-list-item dense class="px-0">
                  <v-list-item-avatar
                    :color="order_table.is_online_order ? 'green' : 'primary'"
                    size="40"
                  >
                    <v-icon small dark>
                      {{
                        order_table.is_online_order
                          ? "fal fa-globe"
                          : "fal fa-store"
                      }}
                    </v-icon>
                  </v-list-item-avatar>
                  <v-list-item-content>
                    <v-list-item-title>
                      <v-chip
                        :color="orderStatusColor(order_table.status)"
                        :text-color="
                          orderStatusColor(order_table.status) === 'grey'
                            ? 'black'
                            : 'white'
                        "
                        x-small
                        :class="`status-chip status-indicator-order status-${order_table.status} mr-2`"
                      >
                        <v-icon x-small left class="mr-1">
                          {{
                            getStatusColorByType(order_table.status, "order")
                              .icon
                          }}
                        </v-icon>
                        {{ $t(order_table.status) }}
                      </v-chip>
                      {{ order_table.code }}
                    </v-list-item-title>
                    <v-list-item-subtitle
                      >{{ order_table.created_at | moment }}
                    </v-list-item-subtitle>
                  </v-list-item-content>
                  <v-list-item-action
                    class="font-weight-bold text-h4 primary--text"
                  >
                    {{ order_table.total_price | NumberFormat }}
                  </v-list-item-action>
                </v-list-item>
                <v-divider />

                <v-list-item
                  dense
                  v-for="(data, index) in order_table.history"
                  :key="index"
                >
                  <v-list-item-avatar tile>
                    <app-img
                      v-if="data.product_id && data.product_id.img"
                      :img="data.product_id.img"
                      width="40"
                      height="40"
                    />
                  </v-list-item-avatar>
                  <v-list-item-content>
                    <v-list-item-title>
                      {{
                        data.product_id
                          ? getProductDisplayName(data.product_id)
                          : $t("unknown_product")
                      }}
                    </v-list-item-title>
                    <v-list-item-subtitle>
                      <template v-if="isBuffetIncludedLine(data)">
                        <span class="mr-1">{{ data.qty }}</span>
                        <v-chip
                          x-small
                          label
                          outlined
                          color="primary"
                          class="rounded-lg"
                        >
                          {{ $t("free") }}
                        </v-chip>
                      </template>
                      <template v-else>
                        {{ data.qty }} x {{ data.price_sale | NumberFormat }}
                      </template>
                      <span
                        v-if="data.product_variant && data.product_variant.sku"
                        class="text-caption"
                      >
                        • {{ $t("sku") }}: {{ data.product_variant.sku }}
                      </span>
                    </v-list-item-subtitle>
                  </v-list-item-content>
                  <v-list-item-action
                    class="mr-6 primary--text font-weight-bold"
                  >
                    <v-chip
                      v-if="isBuffetIncludedLine(data)"
                      x-small
                      label
                      outlined
                      color="primary"
                      class="rounded-lg"
                    >
                      {{ $t("free") }}
                    </v-chip>
                    <template v-else>
                      {{ data.total_price | NumberFormat }}
                    </template>
                  </v-list-item-action>
                  <v-list-item-action>
                    <v-row class="d-flex justify-between">
                      <!-- cancel -->
                      <v-btn
                        v-if="data.status !== 'cancel'"
                        icon
                        class="mr-2"
                        :disabled="allItemsCanceled"
                        @click="updateStatusItem(data, 'cancel')"
                      >
                        <v-icon color="red"> fas fa-times </v-icon>
                      </v-btn>
                      <!--  delivered-->
                      <v-btn
                        v-if="data.status !== 'delivered'"
                        icon
                        class="ml-2"
                        :disabled="allItemsDelivered"
                        @click="updateStatusItem(data, 'delivered')"
                      >
                        <v-icon color="green"> fas fa-check </v-icon>
                      </v-btn>
                    </v-row>
                  </v-list-item-action>
                </v-list-item>
              </v-card-text>
              <v-divider />
              <v-card-actions>
                <AppAction
                  :item="order_table"
                  :items="actionList"
                  @onConfirm="onConfirm"
                  @checkBill="checkBill"
                  @moveMerge="fetchTable"
                />
              </v-card-actions>
            </v-card>
          </v-col>
        </v-row>
      </div>
    </app-custom-dialog>

    <!-- AppLoading -->
    <app-loading :overlay="overlay" />

    <!-- print -->
    <app-bill v-show="print" :item="printBill" />

    <!-- print QR -->
    <qr-print
      v-show="showPrintQR"
      :qr="printQRData.qr"
      :table-name="printQRData.tableName"
      :store-name="printQRData.storeName"
      :store-logo="printQRData.storeLogo"
      :is-buffet="printQRData.isBuffet"
      :buffet-plans="printQRData.buffetPlans"
      :start-time="printQRData.startTime"
      :end-time="printQRData.endTime"
    />

    <!-- Discount Modal -->
    <app-custom-dialog
      v-model="discountModal"
      title="discount_modal_title"
      width="350px"
      :footer="false"
    >
      <template slot="activator">
        <DiscountModal
          :total-before-discount="totalPriceBeforeDiscount"
          :discount-type="appliedDiscount?.type || 'amount'"
          :initial-amount="appliedDiscount?.value?.toString() || '0'"
          :currency-symbol="currencySymbol?.symbol || '₭'"
          :show-total="true"
          submit-button-text="apply"
          @apply="handleDiscountApply"
          @close="discountModal = false"
        />
      </template>
    </app-custom-dialog>

    <!-- Move Order Dialog -->
    <app-custom-dialog
      v-model="moveOrderModal"
      :footer="true"
      title="select_target_table"
      width="1200px"
      @submit="submitMoveOrder"
    >
      <div slot="activator">
        <!-- Fixed search and filters at top -->
        <div class="move-order-search-container">
          <v-row justify="space-between" align="end">
            <v-col cols="12" md="4">
              <TextSelect
                v-model="moveOrderStatusFilter"
                :items="translatedStatusList"
                :dense="true"
                :placeholder="true"
                text="text"
                itemValue="value"
                :returnObject="false"
                label="filter_by_status"
              />
            </v-col>
            <v-col cols="12" md="4">
              <TextSelect
                v-model="moveOrderZoneFilter"
                :items="translatedZoneList"
                :dense="true"
                :placeholder="true"
                text="name"
                itemValue="id"
                :returnObject="false"
                label="filter_by_zone"
              />
            </v-col>
            <v-col cols="12" md="4">
              <TextSearch
                v-model="moveOrderSearch"
                :placeholder="$t('search_tables')"
                class="move-order-search"
              />
            </v-col>
          </v-row>
        </div>
        <div v-if="loadingTablesForMove" class="text-center pa-4">
          <v-progress-circular
            indeterminate
            color="primary"
          ></v-progress-circular>
          <div class="mt-2">{{ $t("loading_tables") }}</div>
        </div>
        <div v-else class="move-order-tables-container">
          <v-row>
            <v-col
              v-for="table in availableTablesForMove"
              :key="table.id"
              cols="12"
              sm="6"
              md="4"
              lg="2"
              xl="2"
              class="pa-2"
            >
              <v-card
                :class="[
                  'table-card-move',
                  { 'selected-table': selectedMoveTargetTable === table.id },
                ]"
                outlined
                @click="selectMoveTargetTable(table.id)"
                :elevation="selectedMoveTargetTable === table.id ? 4 : 1"
              >
                <v-card-text class="text-center pa-3">
                  <div class="text-h6 font-weight-bold mb-2">
                    {{ table.name }}
                  </div>
                  <v-chip
                    :color="tableStatusColor(table.status)"
                    small
                    text-color="white"
                    class="mb-2"
                  >
                    {{ $t(table.status) }}
                  </v-chip>
                  <div v-if="table.zone" class="text-caption grey--text mb-1">
                    {{ table.zone.name }}
                  </div>
                </v-card-text>
              </v-card>
            </v-col>
          </v-row>
          <v-alert v-if="availableTablesForMove.length === 0" type="info" text>
            {{
              moveOrderSearch || moveOrderStatusFilter || moveOrderZoneFilter
                ? $t("no_tables_match_filters")
                : $t("no_other_tables_found")
            }}
          </v-alert>
        </div>
      </div>
    </app-custom-dialog>

    <BuffetPlanPicker
      v-model="buffetSetupModal"
      :initial-lines="buffetSetupInitialLines"
      @applied="submitBuffetSetup"
    />
  </div>
</template>

<script>
import { TABLE_VALIDATE } from "@/validations/shop";
import ZONE_VALIDATE from "@/validations/zone";
import { myMixin } from "@/mixin/main";
import { tableManagementMixin } from "@/mixin/tableManagement";
import TextSelect from "@/components/global/TextSelect";
import TextSearch from "@/components/global/TextSearch";
import TTextField from "@/components/global/TextField";
import AppAction from "@/components/tables/action";
import AppChooseTable from "@/components/restaurant/chooseTable";
import UnifiedPaymentPad from "@/components/payment/UnifiedPaymentPad.vue";
import DiscountModal from "@/components/layouts/DiscountModal.vue";
import AppCustomer from "@/components/customers/CustomerSelector.vue";
import NumericExchangeRate from "@/components/NumericExchangeRate.vue";
import AppTextArea from "@/components/global/TextArea";
import AppBill from "@/components/print/bill";
import QrPrint from "@/components/print/QrPrint.vue";
import draggable from "vuedraggable";
import BuffetPlanPicker from "@/components/sale/BuffetPlanPicker.vue";

export default {
  name: "TablesDesktop",
  mixins: [TABLE_VALIDATE, ZONE_VALIDATE, myMixin, tableManagementMixin],
  data() {
    return {
      // Desktop-specific properties
      showPrintQR: false,
      printQRData: {
        qr: "",
        tableName: "",
        storeName: "",
        storeLogo: null,
        isBuffet: false,
        buffetPlans: [],
        startTime: "--:--",
        endTime: "--:--",
      },
    };
  },
  components: {
    TextSelect,
    TextSearch,
    TTextField,
    AppAction,
    AppChooseTable,
    UnifiedPaymentPad,
    DiscountModal,
    AppCustomer,
    NumericExchangeRate,
    AppTextArea,
    AppBill,
    "qr-print": QrPrint,
    draggable,
    BuffetPlanPicker,
  },
  computed: {
    // Summary row in the same KpiCards style as /products (statisticsArray is shared with the mobile view)
    tableKpiItems() {
      return this.statisticsArray.map((stat) => ({
        key: stat.key,
        title: stat.label,
        icon: stat.icon,
        value: Number(this.statistics[stat.key]) || 0,
      }));
    },
    group1Buttons() {
      if (this.isBuffet) {
        return [
          {
            key: "print_kitchen",
            label: "print_kitchen",
            icon: "fal fa-utensils",
            method: "printOrderToKitchen",
            group: 1,
          },
          {
            key: "open_table",
            label: "buffet_open_table",
            icon: "fal fa-door-open",
            method: "openTableBuffet",
            group: 1,
          },
          {
            key: "print_receipt",
            label: "print_receipt",
            icon: "fal fa-receipt",
            method: "printOrderReceipt",
            group: 1,
          },
          {
            key: "discount",
            label: "discount",
            icon: "fal fa-percent",
            method: "applyDiscount",
            group: 1,
          },
          {
            key: "checkout",
            label: "checkout",
            icon: "fal fa-credit-card",
            method: "checkout",
            group: 2,
            color: "primary",
          },
          {
            key: "add_order",
            label: "add_order",
            icon: "fal fa-plus",
            method: "addOrder",
            group: 2,
          },
        ];
      }
      return [
        {
          key: "print_kitchen",
          label: "print_kitchen",
          icon: "fal fa-utensils",
          method: "printOrderToKitchen",
          group: 1,
        },
        {
          key: "print_receipt",
          label: "print_receipt",
          icon: "fal fa-receipt",
          method: "printOrderReceipt",
          group: 1,
        },
        {
          key: "discount",
          label: "discount",
          icon: "fal fa-percent",
          method: "applyDiscount",
          group: 1,
        },
        {
          key: "checkout",
          label: "checkout",
          icon: "fal fa-credit-card",
          method: "checkout",
          group: 2,
          color: "primary",
        },
        {
          key: "add_order",
          label: "add_order",
          icon: "fal fa-plus",
          method: "addOrder",
          group: 2,
        },
        {
          key: "move_order",
          label: "move_order",
          icon: "fal fa-exchange-alt",
          method: "moveOrder",
          group: 2,
        },
      ];
    },
    secondaryButtons() {
      return this.group1Buttons.filter((b) => b.group === 1);
    },
    primaryButtons() {
      return this.group1Buttons.filter((b) => b.group === 2);
    },
    totalBuffetPersons() {
      return (this.order_table?.buffetPlans || []).reduce(
        (sum, p) => sum + (Number(p.persons) || 0),
        0
      );
    },
    totalSidebarLineCount() {
      if (!this.order_table) {
        return 0;
      }
      // Count only history items — buffet plans are displayed in their own separate block
      // and should not be included in the order-items chip count.
      return this.order_table.history?.length || 0;
    },
    /** Show "No orders" only when there are no history rows and no buffet plan lines */
    showDesktopNoProductLinesEmpty() {
      const hasHist = (this.order_table?.history?.length || 0) > 0;
      const hasBuffetLines =
        this.isBuffet &&
        this.order_table?.buffetPlans &&
        this.order_table.buffetPlans.length > 0;
      return !hasHist && !hasBuffetLines;
    },
  },
  methods: {
    // Chairs around the table from its seat count (default 4): top/bottom rows first,
    // side chairs appear from 5 seats up (1 left, then 1 right).
    seatLayout(item) {
      const seats = Math.min(20, Math.max(1, parseInt(item && item.seats, 10) || 4));
      const left = seats >= 5 ? 1 : 0;
      const right = seats >= 6 ? 1 : 0;
      const rest = seats - left - right;
      return { left, right, top: Math.ceil(rest / 2), bottom: Math.floor(rest / 2) };
    },
    getProductDisplayName(product) {
      const locale = this.$i18n?.locale;
      let nameKey = "name_la";
      if (locale === "en") nameKey = "name_en";
      if (locale === "th") nameKey = "name_th";
      if (locale === "cn" || locale === "ch") nameKey = "name_cn";
      if (locale === "la") nameKey = "name_la";

      return product?.[nameKey] || product?.name || this.$t("unknown_product");
    },
    // Override handleDiscountApply to work with new DiscountModal component
    handleDiscountApply(discountData) {
      try {
        if (!discountData) {
          toast(this, "invalid_discount_value", "error");
          return;
        }

        this.appliedDiscount = discountData;
        this.discountModal = false;
        toast(this, "discount_applied_success", "success");
      } catch (error) {
        console.error("Error applying discount:", error);
        toast(this, "error_applying_discount", "error");
      }
    },
    navigateToZones() {
      this.$router.push({ name: "zones" });
    },
    async handleQuantityDialogSubmit() {
      if (this.$refs.leftoverQuantityDialogRef) {
        const adjustedItems =
          this.$refs.leftoverQuantityDialogRef.getAdjustedItems();
        await this.confirmLeftoverQuantities(adjustedItems);
      }
    },
    async toggleQrOrder() {
      try {
        if (!this.form || !this.form.id) {
          return;
        }
        const { data } = await this.$http.put(`store_table/${this.form.id}`, {
          allow_qr_order: this.form.allow_qr_order ? 1 : 0,
          store_id: this.form.store_id,
          branch_id: this.form.branch_id,
        });
        if (data.status) {
          // Update local form state
          this.form.allow_qr_order = data.data.allow_qr_order;
          // Update in items.data if exists
          const index = this.items.data?.findIndex(
            (item) => item.id === this.form.id,
          );
          if (index !== -1 && this.items.data) {
            this.$set(
              this.items.data[index],
              "allow_qr_order",
              data.data.allow_qr_order,
            );
          }
          // Update in draggableTables if exists
          const dragIndex = this.draggableTables?.findIndex(
            (item) => item.id === this.form.id,
          );
          if (dragIndex !== -1 && this.draggableTables) {
            this.$set(
              this.draggableTables[dragIndex],
              "allow_qr_order",
              data.data.allow_qr_order,
            );
          }
          // Show success message
          if (this.$toast) {
            this.$toast.success(data.message || "QR order setting updated");
          }
        }
      } catch (error) {
        console.error("Failed to toggle QR order:", error);
        // Revert the switch if update failed
        this.form.allow_qr_order = !this.form.allow_qr_order;
        if (this.$toast) {
          this.$toast.error(
            error?.response?.data?.message ||
              "Failed to update QR order setting",
          );
        }
      }
    },
    async printQR() {
      try {
        if (!this.qr || !this.form) {
          return;
        }

        const storeName = this.user?.stores?.name || "";
        const logoVal = this.user?.stores?.logo;
        const storeLogo = logoVal ? this.IMAGES(logoVal) : null;
        const tableName = this.form.name || this.$t("table");

        // Buffet session info — fetch fresh order data since generateQR does not load order_table
        let buffetPlans = [];
        let startTime = "--:--";
        let endTime = "--:--";

        if (this.isBuffet && this.form.id) {
          try {
            const { data } = await this.$http.get(`get_table_order/${this.form.id}`);
            const ot = data?.data;
            if (ot) {
              buffetPlans = ot.buffetPlans || ot.buffet_plans || [];
              if (ot.start_time) {
                startTime = ot.start_time;
              } else if (ot.created_at) {
                const d = new Date(ot.created_at);
                startTime = `${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;
              }
              if (ot.end_time) {
                endTime = ot.end_time;
              } else if (ot.created_at) {
                const d = new Date(ot.created_at);
                const duration = Number(ot.buffet_duration) || 120;
                d.setMinutes(d.getMinutes() + duration);
                endTime = `${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;
              }
            }
          } catch (fetchErr) {
            // silent — print without buffet info if fetch fails
          }
        }

        // Set print data
        this.printQRData = {
          qr: this.qr,
          tableName: tableName,
          storeName: storeName,
          storeLogo: storeLogo,
          isBuffet: this.isBuffet,
          buffetPlans: buffetPlans,
          startTime: startTime,
          endTime: endTime,
        };

        // Close the modal and show print component
        this.show_qr = false;
        this.showPrintQR = true;

        // Wait for component to render, then print using htmlToPaper
        setTimeout(() => {
          setTimeout(() => {
            this.$htmlToPaper("printQR");
            setTimeout(() => {
              this.showPrintQR = false;
            }, 1000);
          }, 500);
        }, 500);
      } catch (error) {
        console.error("Failed to print QR code:", error);
        this.showPrintQR = false;
        if (this.$toast) {
          this.$toast.error(
            this.$t("print_error") || "Failed to print QR code",
          );
        }
      }
    },
  },
};
</script>
<style scoped>
/* Tables Page Layout Structure */
.tables-page-container {
  height: calc(100vh - 100px); /* Subtract app bar height */
  display: flex;
  flex-direction: column;
  overflow: hidden;
  /* margin-top: 65px;  */
}

/* Main Content Area */
.main-content-area {
  flex: 1;
  overflow: hidden;
  display: flex;
  flex-direction: column;
}

.content-container {
  height: 100%;
  padding: 0;
}

.content-row {
  height: 100%;
}

/* Left Panel - Stats, Filter, and Scrollable Table List */
.left-panel {
  height: 100%;
  padding-right: 12px;
  display: flex;
  flex-direction: column;
}

/* Fixed Statistics Section */
.fixed-stats-section {
  flex-shrink: 0;
  z-index: 2;
  padding: 0;
  margin-bottom: 8px;
}

/* Fixed Filter Section */
.fixed-filter-section {
  flex-shrink: 0;
  z-index: 2;
  padding-top: 0;
}

.table-cards-container {
  flex: 1;
  overflow-y: auto;
  padding: 12px;
}

/* Right Panel - Full Height Table Details */
.right-panel {
  height: 100%;
  padding-left: 12px;
}

.right-panel-card {
  height: 100%;
  display: flex;
  flex-direction: column;
}

/* Modern styling for restaurant table management */

/* Filter Card */
.v-card {
  border: none !important;
  overflow: hidden;
  transition: all 0.3s ease;
  border-radius: 12px !important;
}


.v-card-title {
  padding-top: 20px;
  padding-bottom: 12px;
}

/* Status Chips */
.v-chip.v-chip {
  font-weight: 500 !important;
  border-radius: 20px !important;
  transition: all 0.2s ease;
  box-shadow: none !important;
  border: 1px solid #e0e0e0 !important;
  height: 32px;
}

.v-chip.v-chip--active {
  box-shadow: 0 2px 5px rgba(0, 0, 0, 0.1) !important;
  transform: translateY(-1px);
}

/* New Data Button */
.v-btn.v-btn--depressed.v-btn--has-bg {
  border-radius: 8px !important;
  text-transform: none !important;
  font-weight: 600 !important;
  letter-spacing: 0 !important;
  box-shadow: 0 4px 10px rgba(0, 0, 0, 0.1) !important;
  transition: transform 0.2s ease, box-shadow 0.2s ease;
}

.v-btn.v-btn--depressed.v-btn--has-bg:hover {
  transform: translateY(-2px);
  box-shadow: 0 6px 15px rgba(0, 0, 0, 0.15) !important;
}

/* Table Cards */
.table-card {
  border-radius: 16px !important;
  height: 100%;
  position: relative;
  overflow: hidden;
  border: 2px solid var(--v-border-base, rgba(0, 0, 0, 0.08)) !important;
  cursor: pointer;
  transition: border-color 0.2s ease;
}

/* New Order Indicator */
.new-order-indicator {
  position: absolute;
  top: 8px;
  right: 8px;
  width: 12px;
  height: 12px;
  background-color: #4caf50;
  border-radius: 50%;
  border: 2px solid white;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
  z-index: 10;
  animation: pulse 2s infinite;
}

@keyframes pulse {
  0% {
    transform: scale(1);
    opacity: 1;
  }
  50% {
    transform: scale(1.1);
    opacity: 0.8;
  }
  100% {
    transform: scale(1);
    opacity: 1;
  }
}

.table-card:hover {
  border-color: #5b39c9 !important;
}

/* Status-based background colors */
.table-card-available {
  border-left: 4px solid #4caf50 !important;
}

.table-card-busy {
  border-left: 4px solid #ff9800 !important;
}

.table-card-reserved {
  border-left: 4px solid #ffc107 !important;
}

.table-card-pending {
  border-left: 4px solid #2196f3 !important;
}

/* Empty State Centering */
.empty-state-centered {
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  padding: 24px 16px;
}

.empty-state-icon-wrap {
  width: 64px;
  height: 64px;
  border-radius: 50%;
  background: rgba(91, 57, 201, 0.08);
  display: flex;
  align-items: center;
  justify-content: center;
}

.theme--dark .empty-state-icon-wrap {
  background: rgba(91, 57, 201, 0.18);
}

/* Enhanced Sidebar Styles */
.enhanced-sidebar {
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;
  overflow: hidden;
}

.order-detail-section {
  flex-shrink: 0; /* Fixed position */
  background: transparent;
}

/* ── Unified order info card ── */
.order-info-card {
  border: 1px solid rgba(91, 57, 201, 0.18);
  border-radius: 10px;
  overflow: hidden;
  background: #ffffff;
}

.theme--dark .order-info-card {
  border-color: rgba(91, 57, 201, 0.3);
  background: #1e1e1e;
}

.order-info-header {
  background: rgba(91, 57, 201, 0.03);
}

.theme--dark .order-info-header {
  background: rgba(91, 57, 201, 0.07);
}

.order-name-badge {
  display: inline-flex;
  align-items: center;
  background-color: var(--v-primary-base, #5b39c9);
  color: white;
  font-weight: 700;
  font-size: 12px;
  padding: 3px 10px;
  border-radius: 999px;
  white-space: nowrap;
  flex-shrink: 0;
}

.order-txn-code {
  font-family: "Courier New", monospace;
  font-size: 11px;
  color: rgba(0, 0, 0, 0.45);
  letter-spacing: 0.4px;
  font-weight: 500;
}

.theme--dark .order-txn-code {
  color: rgba(255, 255, 255, 0.45);
}

.order-meta-time {
  font-size: 11px;
  color: rgba(0, 0, 0, 0.4);
  white-space: nowrap;
}

.theme--dark .order-meta-time {
  color: rgba(255, 255, 255, 0.4);
}

/* ── Totals block ── */
.totals-sub-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 2px 0;
}

.totals-sub-label {
  font-size: 12px;
  color: #757575;
}

.theme--dark .totals-sub-label {
  color: #9e9e9e;
}

.totals-sub-value {
  font-size: 12px;
  font-weight: 500;
  color: #424242;
}

.theme--dark .totals-sub-value {
  color: #e0e0e0;
}

.totals-grand-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.totals-grand-row.has-divider {
  padding-top: 6px;
  margin-top: 4px;
  border-top: 1px dashed rgba(0, 0, 0, 0.1);
}

.theme--dark .totals-grand-row.has-divider {
  border-top-color: rgba(255, 255, 255, 0.1);
}

.totals-grand-label {
  font-size: 13px;
  font-weight: 600;
  color: #212121;
}

.theme--dark .totals-grand-label {
  color: #ffffff;
}

.totals-grand-value {
  font-size: 16px;
  font-weight: 700;
}

.buffet-plans-block {
  border-bottom: 1px solid rgba(0, 0, 0, 0.06);
  margin-bottom: 8px;
}

.buffet-plans-block--card {
  background-color: rgba(25, 118, 210, 0.04);
  border-left: 4px solid var(--v-primary-base);
  border-bottom: none;
  transition: background-color 200ms ease;
}

.theme--dark .buffet-plans-block--card {
  background-color: rgba(33, 150, 243, 0.08);
}

.theme--dark .buffet-plans-block {
  border-bottom-color: rgba(255, 255, 255, 0.08);
}

.buffet-plan-row {
  padding: 8px 0;
  border-bottom: 1px solid rgba(0, 0, 0, 0.06);
}

.buffet-plan-row:last-child {
  border-bottom: 0;
}

.theme--dark .buffet-plan-row {
  border-bottom-color: rgba(255, 255, 255, 0.08);
}

.buffet-plan-row__left {
  min-width: 0;
}

/* Two-tier action button sections */
.action-bar-secondary {
  flex-shrink: 0;
}

.secondary-action-btn {
  height: 54px !important;
  border-color: rgba(0, 0, 0, 0.12) !important;
  color: rgba(0, 0, 0, 0.65) !important;
  font-size: 11px !important;
}

.theme--dark .secondary-action-btn {
  border-color: rgba(255, 255, 255, 0.12) !important;
  color: rgba(255, 255, 255, 0.7) !important;
}

.secondary-btn-label {
  font-size: 10px;
  font-weight: 500;
  line-height: 1.2;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 100%;
  text-align: center;
}

.action-bar-primary {
  flex-shrink: 0;
}

.primary-action-btn {
  height: 38px !important;
  font-weight: 600 !important;
  text-transform: none !important;
  letter-spacing: 0 !important;
  font-size: 13px !important;
}


.items-count-row-desktop {
  padding: 6px 0 2px 0;
}

/* Items Count Badge */
.items-count-chip {
  font-weight: 600;
  min-width: 24px;
  height: 20px;
}

.order-items-section {
  flex: 1; /* Takes remaining space */
  display: flex;
  flex-direction: column;
  min-height: 0; /* Important for flex child */
  overflow: hidden;
}

.order-items-card-text {
  flex: 1;
  min-height: 0;
  overflow: hidden;
  display: flex;
  flex-direction: column;
}

.order-items-table {
  flex: 1;
  min-height: 0;
  overflow-y: auto;
}

/* Ensure table headers stay fixed when scrolling inside .order-items-table */
.scrollable-table thead th {
  position: sticky;
  top: 0;
  z-index: 1;
}

/* Enhanced table card styling */
.table-card {
  transition: all 0.3s ease;
  cursor: pointer;
  border: 2px solid transparent;
  margin: 4px;
}

.table-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15) !important;
  border-color: #5b39c9;
  margin: 4px;
}

.table-selected {
  border-color: #5b39c9 !important;
  background-color: rgba(91, 57, 201, 0.05) !important;
}

.table-selected:hover {
  background-color: rgba(91, 57, 201, 0.1) !important;
}

/* Dropdown menu */
.v-list.v-sheet {
  border-radius: 8px !important;
  overflow: hidden;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15) !important;
}

.v-list-item {
  transition: background-color 0.2s ease;
}

/* Responsive adjustments */
@media (max-width: 960px) {
  .table-card {
    padding: 8px;
  }

  .left-panel,
  .right-panel {
    padding: 0 6px;
  }
}

/* Status Color System Styles */
.status-chip {
  font-weight: 600;
  letter-spacing: 0.5px;
  border-radius: 12px;
  transition: all 0.2s ease;
}

.status-chip:hover {
  transform: translateY(-1px);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
}

/* Status-specific colors */
.status-pending {
  background: linear-gradient(135deg, #ff9800, #f57c00) !important;
  color: white !important;
}

.status-cooking {
  background: linear-gradient(135deg, #2196f3, #1976d2) !important;
  color: white !important;
}

.status-preparing {
  background: linear-gradient(135deg, #3f51b5, #303f9f) !important;
  color: white !important;
}

.status-ready {
  background: linear-gradient(135deg, #4caf50, #388e3c) !important;
  color: white !important;
}

.status-overdue {
  background: linear-gradient(135deg, #ff5722, #d84315) !important;
  color: white !important;
  animation: pulse 2s infinite;
}

.status-cancel {
  background: linear-gradient(135deg, #f44336, #d32f2f) !important;
  color: white !important;
}

.status-cancelled {
  background: linear-gradient(135deg, #f44336, #d32f2f) !important;
  color: white !important;
}

.status-delivered {
  background: linear-gradient(135deg, #4caf50, #388e3c) !important;
  color: white !important;
}

.status-done {
  background: linear-gradient(135deg, #4caf50, #388e3c) !important;
  color: white !important;
}

.status-new {
  background: linear-gradient(135deg, #5b39c9, #4527a0) !important;
  color: white !important;
}

/* Animation for overdue status */
@keyframes pulse {
  0% {
    box-shadow: 0 0 0 0 rgba(255, 87, 34, 0.7);
  }
  70% {
    box-shadow: 0 0 0 10px rgba(255, 87, 34, 0);
  }
  100% {
    box-shadow: 0 0 0 0 rgba(255, 87, 34, 0);
  }
}

/* Order items table enhancements */
.order-items-table .v-data-table td {
  vertical-align: middle;
}

.order-items-table .status-chip {
  margin: 0;
}

/* Dark mode support for status colors */
.theme--dark .status-chip {
  border: 1px solid rgba(255, 255, 255, 0.1);
}

/* Mobile responsive adjustments */
@media (max-width: 599px) {
  .empty-state-centered {
    height: auto;
    min-height: 300px;
  }

  .content-container {
    padding: 8px;
  }

  .table-cards-container {
    padding: 8px;
  }

  .status-chip {
    font-size: 11px;
    height: 22px;
  }
}

/* Drag and Drop Styles */
.drag-handle {
  cursor: grab;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  border-radius: 4px;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.drag-handle:hover {
  background-color: rgba(0, 0, 0, 0.1);
  transform: scale(1.1);
}

.drag-handle:active {
  cursor: grabbing;
  transform: scale(0.95);
}

.drag-icon {
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.drag-handle:hover .drag-icon {
  color: var(--v-primary-base, #5b39c9) !important;
  transform: scale(1.2);
}

.table-card {
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  will-change: transform, box-shadow, opacity;
}

.table-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
}

.table-card.dragging-active {
  opacity: 0.7;
  transform: scale(0.98) rotate(2deg);
  box-shadow: 0 12px 24px rgba(0, 0, 0, 0.25);
  z-index: 1000;
  transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
}

/* Vue.Draggable Classes */
.table-ghost {
  opacity: 0.3;
  background: linear-gradient(135deg, #f5f5f5 0%, #e8e8e8 100%);
  border: 2px dashed var(--v-primary-base, #5b39c9);
  border-radius: 8px;
  transform: scale(0.95);
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.table-chosen {
  cursor: grabbing !important;
  transform: scale(1.02);
  box-shadow: 0 8px 16px rgba(25, 118, 210, 0.2);
  transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
}

.table-drag {
  transform: rotate(3deg) scale(1.05);
  box-shadow: 0 16px 32px rgba(0, 0, 0, 0.3);
  z-index: 1000;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  background: white;
  border-radius: 8px;
}

@keyframes dragPulse {
  0% {
    box-shadow: 0 0 0 0 rgba(25, 118, 210, 0.4);
  }
  70% {
    box-shadow: 0 0 0 10px rgba(25, 118, 210, 0);
  }
  100% {
    box-shadow: 0 0 0 0 rgba(25, 118, 210, 0);
  }
}

/* Drag handle animations */
.drag-handle {
  position: relative;
  overflow: hidden;
}

.drag-handle::before {
  content: "";
  position: absolute;
  top: 50%;
  left: 50%;
  width: 0;
  height: 0;
  background: rgba(25, 118, 210, 0.2);
  border-radius: 50%;
  transform: translate(-50%, -50%);
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.drag-handle:hover::before {
  width: 40px;
  height: 40px;
  animation: dragPulse 1.5s infinite;
}

/* Table card drag states */
.table-card.sortable-ghost {
  opacity: 0.4;
  background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
  border: 2px dashed var(--v-primary-base, #5b39c9);
  transform: scale(0.95) rotate(1deg);
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.table-card.sortable-chosen {
  transform: scale(1.02) rotate(1deg);
  box-shadow: 0 12px 24px rgba(25, 118, 210, 0.2);
  transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
}

.table-card.sortable-drag {
  transform: rotate(3deg) scale(1.05);
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.3);
  z-index: 1000;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  background: white;
  border-radius: 12px;
}

/* Move Order Dialog Styles */
.table-card-move {
  cursor: pointer;
  transition: all 0.2s ease;
}

.table-card-move:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
}

.table-card-move.selected-table {
  border: 2px solid #5b39c9 !important;
  box-shadow: 0 0 0 1px #5b39c9 !important;
}

/* Move Order Search Styles */
.move-order-search-container {
  position: sticky;
  top: 0;
  z-index: 10;
  padding: 16px;
  border-bottom: 1px solid #e0e0e0;
  margin-bottom: 20px;
}

.move-order-tables-container {
  max-height: 400px;
  overflow-y: auto;
  padding: 0 16px;
}

/* QR Dialog Styles */
.qr-dialog-content {
  text-align: center;
}

.qr-code-wrapper {
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 10px;
  background: #f5f5f5;
  border-radius: 8px;
  margin: 0 auto;
}

.qr-code-container {
  position: relative;
  display: inline-block;
}

.qr-code-image {
  display: block;
}

.qr-center-logo {
  width: 50px;
  height: 50px;
  object-fit: contain;
  display: block;
}

/* ---------------------------------------------------------------------------
 * PosSmart v2 look — matches the sale page product card (components/products/card.vue):
 * 16px radius, 1px slate border, 8px padding, purple hover/selected border,
 * pill badge, tabular-nums price. Kept last so it wins over the legacy rules above.
 * ------------------------------------------------------------------------- */
.v-card.table-card.ps-table-card {
  border: 1px solid #e2e8f0 !important;
  border-radius: 16px !important;
  box-shadow: none !important;
  background: #fff;
}
.v-card.table-card.ps-table-card:hover {
  transform: none;
  box-shadow: none !important;
  border-color: #5b39c9 !important;
}
.v-card.table-card.ps-table-card:active {
  transform: scale(0.98);
}
.v-card.table-card.ps-table-card {
  display: flex;
  flex-direction: column;
  height: 176px;
  padding: 10px 10px 4px;
  margin: 4px;
}
.v-card.table-card.ps-table-card .v-card__text {
  flex: 1 1 auto;
}
.v-card.table-card.ps-table-card .v-card__actions {
  flex: 0 0 auto;
}
.v-card.table-card.ps-table-card.table-selected {
  border-color: #5b39c9 !important;
  background: #f3f0fc !important;
}
.ps-table-card__name,
.ps-table-card__zone,
.ps-table-card__price {
  max-width: 100%;
}
.ps-table-card__name {
  font-size: 15px;
  text-align: center;
  font-weight: 600;
  line-height: 1.3;
  color: #141021;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.ps-table-card__zone {
  text-align: center;
  margin-top: 2px;
  font-size: 10.5px;
  color: #64748b;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.ps-table-card__price {
  text-align: center;
  margin-top: 4px;
  max-width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-size: 14px;
  font-weight: 500;
  font-variant-numeric: tabular-nums;
  color: #141021;
}
.ps-table-card__note {
  font-size: 11px;
  color: #64748b;
}
.theme--dark .v-card.table-card.ps-table-card {
  background: #1e1e2d;
  border-color: rgba(255, 255, 255, 0.08) !important;
}
.theme--dark .v-card.table-card.ps-table-card.table-selected {
  background: rgba(154, 140, 240, 0.12) !important;
  border-color: #9a8cf0 !important;
}
.theme--dark .ps-table-card__name,
.theme--dark .ps-table-card__price {
  color: #f1f5f9;
}

/* Real-table look: tabletop with chairs, tinted by status */
.ps-table-card .v-card__text {
  position: relative;
}
.ps-table-card__drag {
  position: absolute;
  top: 0;
  left: 0;
  z-index: 1;
}
.ps-table-visual {
  --tbl-fill: #f1f5f9;
  --tbl-line: #cbd5e1;
  --chair: #e2e8f0;
  padding: 8px 6px 0;
}
.ps-table-visual--available {
  --tbl-fill: #ecfdf5;
  --tbl-line: #6ee7b7;
  --chair: #a7f3d0;
}
.ps-table-visual--busy {
  --tbl-fill: #fff7ed;
  --tbl-line: #fdba74;
  --chair: #fdba74;
}
.ps-table-visual--reserved {
  --tbl-fill: #fffbeb;
  --tbl-line: #fcd34d;
  --chair: #fde68a;
}
.ps-table-visual--pending {
  --tbl-fill: #f3f0fc;
  --tbl-line: #c4b5fd;
  --chair: #ddd6fe;
}
.ps-table-visual__chairs {
  display: flex;
  justify-content: space-around;
  gap: 4px;
  height: 12px;
  padding: 0 14px;
}
.ps-table-visual__chairs span {
  flex: 0 1 30px;
  height: 9px;
  background: var(--chair);
  border: 1px solid var(--tbl-line);
}
.ps-table-visual__chairs:first-child span {
  border-radius: 8px 8px 2px 2px;
  margin-bottom: 3px;
}
.ps-table-visual__chairs:last-child span {
  border-radius: 2px 2px 8px 8px;
  margin-top: 3px;
}
.ps-table-visual__top {
  position: relative;
  height: 84px;
  box-sizing: border-box;
  text-align: center;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 6px 8px;
  background: var(--tbl-fill);
  border: 2px solid var(--tbl-line);
  border-radius: 14px;
}
/* side chairs */
.ps-table-visual__side {
  position: absolute;
  top: 50%;
  width: 9px;
  height: 28px;
  margin-top: -14px;
  background: var(--chair);
  border: 1px solid var(--tbl-line);
}
.ps-table-visual__side--l {
  left: -8px;
  border-radius: 8px 2px 2px 8px;
}
.ps-table-visual__side--r {
  right: -8px;
  border-radius: 2px 8px 8px 2px;
}
.theme--dark .ps-table-visual {
  --tbl-fill: rgba(255, 255, 255, 0.05);
  --tbl-line: rgba(255, 255, 255, 0.25);
  --chair: rgba(255, 255, 255, 0.12);
}
.theme--dark .ps-table-visual--available {
  --tbl-fill: rgba(16, 185, 129, 0.12);
  --tbl-line: #34d399;
  --chair: rgba(16, 185, 129, 0.35);
}
.theme--dark .ps-table-visual--busy {
  --tbl-fill: rgba(249, 115, 22, 0.12);
  --tbl-line: #fb923c;
  --chair: rgba(249, 115, 22, 0.35);
}
.theme--dark .ps-table-visual--reserved {
  --tbl-fill: rgba(245, 158, 11, 0.12);
  --tbl-line: #fbbf24;
  --chair: rgba(245, 158, 11, 0.35);
}

/* Simple primary "new data" button */
.v-btn.ps-btn-primary.v-btn--depressed.v-btn--has-bg {
  box-shadow: none !important;
  transform: none;
}
.v-btn.ps-btn-primary.v-btn--depressed.v-btn--has-bg:hover {
  transform: none;
  box-shadow: none !important;
}

/* Table card skeleton (same shape as .ps-table-card) */
.ps-table-card--skeleton {
  border: 1px solid #e2e8f0;
  border-left-width: 4px;
  border-radius: 16px;
  padding: 10px 10px 14px;
  margin: 4px;
  background: #fff;
  height: 176px;
  pointer-events: none;
}
.ps-skeleton-line,
.ps-skeleton-pill {
  background: linear-gradient(90deg, #f1f5f9 25%, #e2e8f0 50%, #f1f5f9 75%);
  background-size: 200px 100%;
  animation: ps-shimmer 1.5s infinite;
}
.ps-skeleton-line {
  height: 14px;
  border-radius: 4px;
}
.ps-skeleton-line--w60 {
  width: 60%;
}
.ps-skeleton-line--w50 {
  width: 50%;
}
.ps-skeleton-line--w40 {
  width: 40%;
}
.ps-skeleton-pill {
  width: 52px;
  height: 20px;
  border-radius: 999px;
  margin-left: 8px;
}
@keyframes ps-shimmer {
  0% {
    background-position: -200px 0;
  }
  100% {
    background-position: calc(200px + 100%) 0;
  }
}
.theme--dark .ps-table-card--skeleton {
  background: #1e1e2d;
  border-color: rgba(255, 255, 255, 0.08);
}
.theme--dark .ps-skeleton-line,
.theme--dark .ps-skeleton-pill {
  background: linear-gradient(
    90deg,
    rgba(255, 255, 255, 0.06) 25%,
    rgba(255, 255, 255, 0.12) 50%,
    rgba(255, 255, 255, 0.06) 75%
  );
  background-size: 200px 100%;
}

/* Right panel actions — PosSmart v2 look (1px slate border, 12px radius, primary accents) */
.right-panel-card.v-card {
  border: 1px solid #e2e8f0 !important;
  border-radius: 16px !important;
  box-shadow: none !important;
}
.v-btn.secondary-action-btn.v-btn--outlined {
  height: 58px !important;
  border: 1px solid #e2e8f0 !important;
  border-radius: 12px !important;
  background: #fff !important;
  color: #475569 !important;
  box-shadow: none !important;
  transform: none;
}
.v-btn.secondary-action-btn.v-btn--outlined:hover:not(.v-btn--disabled) {
  border-color: #5b39c9 !important;
  color: #5b39c9 !important;
  transform: none;
  box-shadow: none !important;
}
.secondary-action-icon {
  width: 26px;
  height: 26px;
  border-radius: 8px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: rgba(91, 57, 201, 0.1);
}
.secondary-action-icon .v-icon {
  color: #5b39c9 !important;
}
.v-btn--disabled .secondary-action-icon {
  background: #f1f5f9;
}
.v-btn--disabled .secondary-action-icon .v-icon {
  color: #94a3b8 !important;
}
.v-btn.primary-action-btn {
  height: 42px !important;
  border-radius: 12px !important;
  font-weight: 600 !important;
  font-size: 13px !important;
  text-transform: none !important;
  letter-spacing: 0 !important;
  box-shadow: none !important;
  transform: none;
}
.v-btn.primary-action-btn.v-btn--outlined {
  border: 1px solid #c4b5fd !important;
  color: #5b39c9 !important;
  background: #fff !important;
}
.v-btn.primary-action-btn.v-btn--outlined:hover:not(.v-btn--disabled) {
  border-color: #5b39c9 !important;
  background: #f3f0fc !important;
  transform: none;
  box-shadow: none !important;
}
.v-btn.primary-action-btn--solid:hover:not(.v-btn--disabled) {
  transform: none;
  box-shadow: none !important;
  filter: brightness(1.08);
}
.v-btn.primary-action-btn.v-btn--disabled {
  opacity: 0.55;
}
.theme--dark .v-btn.secondary-action-btn.v-btn--outlined,
.theme--dark .v-btn.primary-action-btn.v-btn--outlined {
  background: #1e1e2d !important;
  border-color: rgba(255, 255, 255, 0.12) !important;
  color: #cbd5e1 !important;
}
.theme--dark .v-btn.primary-action-btn.v-btn--outlined {
  color: #c4b5fd !important;
}
.theme--dark .secondary-action-icon {
  background: rgba(154, 140, 240, 0.18);
}
.theme--dark .secondary-action-icon .v-icon {
  color: #c4b5fd !important;
}
.theme--dark .right-panel-card.v-card {
  border-color: rgba(255, 255, 255, 0.08) !important;
}
</style>
